/**
 * Double Row Edge Classification Utilities
 *
 * Creates edge classification for double-row template units.
 * Key difference from single-row: back edges are partition (shared wall with opposite row).
 *
 * Key principle: "Every unit has all its walls. Only one unit RENDERS each shared wall."
 * - Each unit maintains complete boundary for collision detection
 * - Rendering uses classification to avoid duplicate walls
 */

import type { UnitEdge, UnitDoorsConfig } from '../types'

/**
 * Create edges for a unit in a double row layout
 *
 * @param unitIndex - Index of this unit within its row (0-based)
 * @param unitCount - Total number of units in this row
 * @param rowId - Row identifier ('A' or 'B')
 * @param buildingCode - Building code prefix for unit IDs
 * @param doorConfig - Per-wall door configuration
 * @returns Array of 4 edges (front, back, left, right)
 */
export function createDoubleRowEdges(
  unitIndex: number,
  unitCount: number,
  rowId: 'A' | 'B',
  _buildingCode: string,
  doorConfig?: UnitDoorsConfig
): UnitEdge[] {
  const isFirstUnit = unitIndex === 0
  const isLastUnit = unitIndex === unitCount - 1

  // Helper to generate neighbor unit code (same row)
  const neighborCode = (index: number) =>
    `${rowId}-${String(index + 1).padStart(2, '0')}`

  // Helper to generate paired unit code (opposite row)
  const pairedCode = (index: number) => {
    const oppositeRow = rowId === 'A' ? 'B' : 'A'
    return `${oppositeRow}-${String(index + 1).padStart(2, '0')}`
  }

  // Both rows have front='exterior' (doors face away from spine)
  // For the spine wall: Row A renders it (back='exterior'), Row B doesn't (back='internal')
  const frontType: 'exterior' | 'partition' | 'internal' = 'exterior'
  const backType: 'exterior' | 'partition' | 'internal' = rowId === 'A' ? 'exterior' : 'internal'

  // Front edges never shared, back edges shared between rows
  const frontShared = false
  const backShared = true

  // For left/right exterior walls: Row B needs swapped logic due to 180° rotation
  // Row A: unit 0 (first) has right=exterior, unit N (last) has left=exterior
  // Row B: unit 0 (first) has LEFT=exterior, unit N (last) has RIGHT=exterior (swapped!)
  const leftIsExterior = rowId === 'A' ? isLastUnit : isFirstUnit
  const rightIsExterior = rowId === 'A' ? isFirstUnit : isLastUnit

  return [
    // Front edge: EXTERIOR for both rows (doors here)
    // - Row A (no rotation): faces toward camera
    // - Row B (180° rotation): faces away from camera
    {
      side: 'front',
      type: frontType,
      canBeShared: frontShared,
      sharedWith: null,
      openings: doorConfig?.front,
    },

    // Back edge: Spine wall - Row A renders it as exterior, Row B skips (internal)
    // - Row A: faces Row B, renders the shared spine wall
    // - Row B: faces Row A, doesn't render (Row A already did)
    {
      side: 'back',
      type: backType,
      canBeShared: backShared,
      sharedWith: pairedCode(unitIndex),
      openings: doorConfig?.back,
    },

    // Left edge: exterior for end unit, partition for interior units
    // Row A: last unit has exterior left wall
    // Row B: first unit has exterior left wall (after 180° rotation)
    {
      side: 'left',
      type: leftIsExterior ? 'exterior' : 'partition',
      canBeShared: !leftIsExterior,
      sharedWith: leftIsExterior ? null : neighborCode(unitIndex + 1),
      openings: doorConfig?.left,
    },

    // Right edge: exterior for end unit, partition for interior units
    // Row A: first unit has exterior right wall
    // Row B: last unit has exterior right wall (after 180° rotation)
    {
      side: 'right',
      type: rightIsExterior ? 'exterior' : 'partition',
      canBeShared: !rightIsExterior,
      sharedWith: rightIsExterior ? null : neighborCode(unitIndex - 1),
      openings: doorConfig?.right,
    },
  ]
}

/**
 * Determine if this unit should render a shared edge
 *
 * For double-row:
 * - Back edges (shared wall between rows): Row A renders (lower row ID)
 * - Left/right edges (partition between units): Lower index renders
 *
 * @param edge - Edge to check
 * @param rowId - Row identifier ('A' or 'B')
 * @param unitIndex - Index of this unit within its row
 * @returns true if this unit should render the edge
 */
export function shouldRenderDoubleRowEdge(
  edge: UnitEdge,
  rowId: 'A' | 'B',
  _unitIndex: number
): boolean {
  // Always render non-shared edges
  if (!edge.canBeShared || !edge.sharedWith) {
    return true
  }

  // For back edge (shared between rows): Row A renders
  if (edge.side === 'back') {
    return rowId === 'A'
  }

  // For left edge: this unit renders (we have lower index than neighbor on left)
  if (edge.side === 'left') {
    return true
  }

  // For right edge: neighbor (lower index) renders
  if (edge.side === 'right') {
    return false
  }

  // Default: render it
  return true
}

/**
 * Get edges that should be rendered for a unit in double row
 *
 * @param edges - All unit edges
 * @param rowId - Row identifier ('A' or 'B')
 * @param unitIndex - Index of this unit within its row
 * @returns Filtered edges to render
 */
export function getDoubleRowEdgesToRender(
  edges: UnitEdge[],
  rowId: 'A' | 'B',
  unitIndex: number
): UnitEdge[] {
  return edges.filter(edge => shouldRenderDoubleRowEdge(edge, rowId, unitIndex))
}

/**
 * Check if an edge is a shared wall (partition or shared with opposite row)
 */
export function isSharedEdge(edge: UnitEdge): boolean {
  return edge.canBeShared && edge.sharedWith !== null
}

/**
 * Check if an edge is the spine wall (shared between rows A and B)
 */
export function isSpineWall(edge: UnitEdge): boolean {
  return edge.side === 'back' && edge.type === 'partition' && edge.canBeShared
}

/**
 * Get the spine wall edge from a unit's edges
 */
export function getSpineWallEdge(edges: UnitEdge[]): UnitEdge | undefined {
  return edges.find(isSpineWall)
}
